<template>
	<div v-if="!isNarrativeStoryPlaying" class="tutorial-step-container">
		<div
			ref="relativeElement"
			class="relative-element"
			:class="`relative-element--${relativeAlignment}`"
		>
			<slot name="default" />
		</div>
		<teleport to="#click-wrapper">
			<div
				ref="teleportedElement"
				class="tutorial-step"
				:class="`tutorial-step--${type}`"
			>
				<slot name="default" />
				<TutorialArrows
					v-if="type === TutorialStepTypes.ARROW && relativeRect"
					right
					:rightOffset="relativeRect?.width"
				/>
			</div>
		</teleport>
	</div>
</template>

<script lang="ts" setup>
import TutorialArrows from "@/components/TutorialArrows.vue";
import useQuestStore from "@/store/useQuestStore.ts";
import { TutorialStepTypes } from "@/types/ui";
import { storeToRefs } from "pinia";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";

const questStore = useQuestStore();
const { activeNarrativeId } = storeToRefs(questStore);

const isNarrativeStoryPlaying = ref<boolean>(false);

// TODO configurable arrow direction
interface Props {
	type: TutorialStepTypes;
	relativeAlignment?: "left" | "right" | "center";
}

const { type, relativeAlignment = "center" } = defineProps<Props>();

const relativeElement = ref<HTMLElement | null>(null);
const teleportedElement = ref<HTMLElement | null>(null);
const relativeRect = ref<DOMRect | null>(null);

let animationFrameId: number | null = null;

const updateTeleportedPosition = (): void => {
	if (relativeElement.value && teleportedElement.value) {
		const firstChild = relativeElement.value.firstElementChild;
		relativeRect.value =
			firstChild !== null
				? firstChild.getBoundingClientRect()
				: relativeElement.value.getBoundingClientRect();
		teleportedElement.value.style.top = `${relativeRect.value.top}px`;
		teleportedElement.value.style.left = `${relativeRect.value.left}px`;
		teleportedElement.value.style.width = `${relativeRect.value.width}px`;
	}
};

// The position is tracked continuously via requestAnimationFrame to ensure smooth following during parent CSS animations
const trackPositionContinuously = (): void => {
	updateTeleportedPosition();
	animationFrameId = requestAnimationFrame(trackPositionContinuously);
};

const stopTracking = (): void => {
	if (animationFrameId !== null) {
		cancelAnimationFrame(animationFrameId);
		animationFrameId = null;
	}
};

// The narrative state is monitored
watch(
	(): number | null => activeNarrativeId.value,
	(): void => {
		if (activeNarrativeId.value !== null) {
			isNarrativeStoryPlaying.value = true;
			// The tracking loop is stopped to save resources while the step is hidden
			stopTracking();
		} else {
			isNarrativeStoryPlaying.value = false;

			// Tracking is started immediately after the narrative ends to sync with DOM insertion and layout shifts
			if (!animationFrameId) {
				animationFrameId = requestAnimationFrame(
					trackPositionContinuously,
				);
			}
		}
	},
	{ immediate: true },
);

onMounted((): void => {
	if (!isNarrativeStoryPlaying.value && !animationFrameId) {
		animationFrameId = requestAnimationFrame(trackPositionContinuously);
	}
});

onBeforeUnmount((): void => {
	stopTracking();
});
</script>

<style lang="scss" scoped>
.tutorial-step-container {
	flex: 1;
	@include flex-col-center;
	width: 100%;

	.relative-element {
		width: 100%;
		@include flex-col-center;
		flex: 1;
		// NOTE: due to the click bounce of the tutorial button, this cannot be visible but we need it for positioning and other properties
		opacity: 0;

		&--left {
			align-items: flex-start;
		}
		&--right {
			align-items: flex-end;
		}
		&--center {
			align-items: center;
		}
	}
}

.tutorial-step {
	position: absolute;
	// do not change this, it's important for tutorial step to be above all other elements
	z-index: 9999;

	// DON'T REMOVE
	:deep(.text-button) {
		width: 100%;
	}

	&--glow {
		animation: pulse 2.5s infinite;
	}

	@keyframes pulse {
		0% {
			box-shadow: 0 0 0.5rem 0.1rem rgba($color: #feb942, $alpha: 0.8);
		}
		50% {
			box-shadow: 0 0 0.5rem 0.1rem rgba($color: #feb942, $alpha: 0);
		}
		100% {
			box-shadow: 0 0 0.5rem 0.1rem rgba($color: #feb942, $alpha: 0.8);
		}
	}
}
</style>
